import React, { useMemo, useState } from 'react'

import { SxProps } from '@mui/material'

import type { TSavedDashboardFilterDto } from '@homepal/api-contract'
import { ParsedDashboardFilterOptions } from 'types/GlobalDashboardFilter'

import { sortAlphabeticalAsc } from 'utils/sortHelper'

import Box from 'components_new/atoms/Box'
import Link from 'components_new/atoms/Link'
import List from 'components_new/atoms/List'
import ListSubheader from 'components_new/atoms/List/ListSubheader'

import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

import SavedFilterItem from './SavedFilterItem'
import { useTranslation } from 'language/translator'
import { TDashboard } from 'types/GlobalDashboard'

interface SavedFiltersProps {
  dashboard: TDashboard
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  savedDashboardFilters: TSavedDashboardFilterDto[]
  savedDashboardFilter: null | TSavedDashboardFilterDto
  setSavedDashboardFilter: (value: TSavedDashboardFilterDto | null) => void
  sx?: SxProps
}

const SavedFilters = (props: SavedFiltersProps) => {
  const {
    dashboard,
    dashboardFilterOptions,
    savedDashboardFilters,
    savedDashboardFilter,
    setSavedDashboardFilter,
    sx
  } = props

  const { t } = useTranslation()

  const [showMore, setShowMore] = useState<boolean>(false)

  // all filters should be available as dashboard filter.
  const handledSavedDashboardFilters = useMemo(() => {
    const filters: {
      available: TSavedDashboardFilterDto[]
      unavailable: TSavedDashboardFilterDto[]
    } = { available: [], unavailable: [] }

    savedDashboardFilters.forEach((filter) => {
      const unavailable = filter.conditions.some(
        (condition) => !dashboardFilterOptions.get(condition.attribute_id)
      )

      if (unavailable) {
        filters.unavailable.push(filter)
      } else {
        filters.available.push(filter)
      }
    })

    filters.available.sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
    filters.unavailable.sort((a, b) => sortAlphabeticalAsc(a.title, b.title))

    return filters
  }, [savedDashboardFilters, dashboardFilterOptions])

  // empty
  if (savedDashboardFilters.length === 0)
    return (
      <EmptyPlaceholder
        background={true}
        height={128}
        title={t('filters', 'no_saved_filters')}
      />
    )

  // success
  return (
    <Box sx={sx}>
      {/*-- available --*/}
      <List>
        {handledSavedDashboardFilters.available.map((filter) => {
          const selected = filter.id === savedDashboardFilter?.id

          return (
            <SavedFilterItem
              dashboard={dashboard}
              filter={filter}
              key={filter.id}
              onClick={() => setSavedDashboardFilter(filter)}
              selected={selected}
              savedDashboardFilter={savedDashboardFilter}
              setSavedDashboardFilter={setSavedDashboardFilter}
            />
          )
        })}
      </List>

      {/*-- unavailable --*/}
      {handledSavedDashboardFilters.unavailable.length > 0 ? (
        <>
          <List>
            <ListSubheader
              border={true}
              disableSticky={true}
              sx={{ bgcolor: 'transparent', mb: 1 }}
            >
              {t('filters', 'unavailable_filters_title')}
            </ListSubheader>
            {handledSavedDashboardFilters.unavailable.map((filter) => {
              const selected = filter.id === savedDashboardFilter?.id

              return (
                <SavedFilterItem
                  dashboard={dashboard}
                  disabled={true}
                  filter={filter}
                  key={filter.id}
                  onClick={() => setSavedDashboardFilter(filter)}
                  selected={selected}
                  savedDashboardFilter={savedDashboardFilter}
                  setSavedDashboardFilter={setSavedDashboardFilter}
                />
              )
            })}
          </List>

          <Box sx={{ bgcolor: 'action.hover', p: 1 }}>
            <Text color="text.secondary" component="div" variant="caption">
              <Text color="inherit" fontSize="inherit" gutterBottom={true}>
                {t('filters', 'unavailable_filters_description')}{' '}
                {!showMore ? (
                  <Link onClick={() => setShowMore(true)}>
                    {t('global', 'show_more') + '...'}
                  </Link>
                ) : null}
              </Text>
              {showMore ? (
                <>
                  <Text color="inherit" fontSize="inherit" gutterBottom={true}>
                    {t('filters', 'unavailable_filters_reason_title')}
                  </Text>
                  <Text color="inherit" fontSize="inherit" gutterBottom={true}>
                    {t('filters', 'unavailable_filters_reason_description')}
                  </Text>
                  <Text color="inherit" fontSize="inherit" gutterBottom={true}>
                    {t('filters', 'unavailable_filters_instruction') + ' '}
                    <Link onClick={() => setShowMore(false)}>
                      {t('global', 'show_less')}
                    </Link>
                  </Text>
                </>
              ) : null}
            </Text>
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export default SavedFilters
